import { HotelFormData } from '@/types/benefits/hotel/HotelFormData';
import clsx from 'clsx';
import {
  startTransition,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import StyledTooltipContainer from '../../styled/StyledTooltipContainer';
import StyledButton from '../../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import DuplicateIcon from '@/assets/icons/copy-03.svg?react';
import HotelRoomCategoryForm from './HotelRoomCategoryForm';
import { buildEmptyPensionCard } from '@/utils/benefits/hotel/buildEmptyPensionCard';
import { WEEKDAYS } from '@/constants/weekdays';
import { useTranslation } from '@/i18n';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import { useExpertMode } from '../excelPaste/useExpertMode';
import {
  EXPERT_FORM_COLUMN_WIDTH,
  FORM_COLUMN_WIDTH,
} from '../excelPaste/expertLayout';
import { Info } from 'lucide-react';

interface HotelRoomsCategoriesListProps {
  control: Control<HotelFormDataWithTranslations>;
  language: SupportedLocale;
  categoryErrorIndex?: number | null;
  isViewMode?: boolean;
}

const HotelRoomsCategoriesList = ({
  control,
  language,
  categoryErrorIndex,
  isViewMode = false,
}: HotelRoomsCategoriesListProps) => {
  const { t } = useTranslation();
  const isExpertMode = useExpertMode() && !isViewMode;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'hotelRoomCategories',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'hotelRoomCategories',
    });
  const [userSelectedIndex, setUserSelectedIndex] = useState(0);

  const hasNavigatedToErrorRef = useRef(false);
  const prevErrorIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const currentErrorIndex = categoryErrorIndex;
    const isNewError =
      currentErrorIndex !== null &&
      currentErrorIndex !== prevErrorIndexRef.current &&
      !hasNavigatedToErrorRef.current;

    if (isNewError && currentErrorIndex !== undefined) {
      startTransition(() => {
        setUserSelectedIndex(currentErrorIndex);
      });
      hasNavigatedToErrorRef.current = true;
    }

    if (currentErrorIndex !== prevErrorIndexRef.current) {
      if (currentErrorIndex === null) {
        hasNavigatedToErrorRef.current = false;
      }
      prevErrorIndexRef.current = currentErrorIndex ?? null;
    }
  }, [categoryErrorIndex]);

  const selectedCategoryIndex = userSelectedIndex;

  const hotelRoomCategories = useWatch({
    control,
    name: 'hotelRoomCategories',
  });
  const translations = useWatch({ control, name: 'translations' });

  const getCategoryTitle = useCallback(
    (index: number) => {
      const translation =
        translations?.[language]?.hotelRoomCategories?.[index];
      const categoryTranslated =
        translation?.name ?? hotelRoomCategories?.[index]?.name;
      if (
        hotelRoomCategories &&
        hotelRoomCategories.length > index &&
        categoryTranslated
      )
        return categoryTranslated;
      return t('hotels.category_', { index: index + 1 });
    },
    [hotelRoomCategories, language, t, translations],
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      {isExpertMode && (
        <div className="flex gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4 text-sm leading-6 text-brand-700">
          <Info className="size-5 shrink-0 stroke-brand-600" />
          <p>
            <span className="font-semibold">
              {t('hotels.pasteFromExcel.expertMode.bannerTitle')}
            </span>
          </p>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:sticky md:top-20 z-10 md:w-1/4 flex flex-col h-fit">
          {fields.map((item, index) => (
            <div
              key={item.id}
              className={clsx('flex justify-between pr-4 border rounded-md', {
                'bg-brand-50 border-brand-600': index === selectedCategoryIndex,
              })}
            >
              <div
                className="w-full h-full pl-4 py-3 hover:cursor-pointer"
                onClick={() => {
                  setUserSelectedIndex(index);
                  hasNavigatedToErrorRef.current = false;
                }}
              >
                <h2 className="text-base font-medium text-black w-full h-full">
                  {getCategoryTitle(index)}
                </h2>
              </div>
              {!isViewMode && (
                <div className="flex gap-2 w-fit">
                  <StyledTooltipContainer
                    tooltipMessage={t('common.duplicate')}
                  >
                    <DuplicateIcon
                      className="size-5 stroke-gray-500 hover:cursor-pointer"
                      onClick={() => {
                        append({
                          ...hotelRoomCategories[index],
                          name: hotelRoomCategories[index].name
                            ? `${hotelRoomCategories[index].name} (copie)`
                            : hotelRoomCategories[index].name,
                          tarifs: hotelRoomCategories[index].tarifs.map(
                            (tarif) => ({
                              ...tarif,
                              periods: tarif.periods.map((period) => ({
                                ...period,
                                startDate: period.startDate
                                  ? new Date(period.startDate)
                                  : undefined,
                                endDate: period.endDate
                                  ? new Date(period.endDate)
                                  : undefined,
                              })),
                            }),
                          ),
                        } as unknown as HotelFormData['hotelRoomCategories'][number]);

                        const categoryTranslations = (lang: SupportedLocale) =>
                          translations?.[lang]?.hotelRoomCategories?.[index];

                        appendTranslation((lang) => ({
                          name:
                            categoryTranslations(lang)?.name ??
                            hotelRoomCategories[index].name ??
                            undefined,
                          shortDescription:
                            categoryTranslations(lang)?.shortDescription ??
                            hotelRoomCategories[index].shortDescription ??
                            undefined,
                          longDescription:
                            categoryTranslations(lang)?.longDescription ??
                            hotelRoomCategories[index].longDescription ??
                            undefined,
                        }));

                        setUserSelectedIndex(fields.length);
                        hasNavigatedToErrorRef.current = false;
                      }}
                    />
                  </StyledTooltipContainer>
                  {fields.length > 1 && (
                    <StyledTooltipContainer tooltipMessage={t('common.delete')}>
                      <TrashIcon
                        className="size-5 stroke-gray-500 hover:cursor-pointer"
                        onClick={() => {
                          remove(index);
                          removeTranslation(index);
                          if (userSelectedIndex >= index) {
                            setUserSelectedIndex((current) => {
                              hasNavigatedToErrorRef.current = false;
                              return Math.max(current - 1, 0);
                            });
                          }
                        }}
                      />
                    </StyledTooltipContainer>
                  )}
                </div>
              )}
            </div>
          ))}
          {!isViewMode && (
            <StyledButton
              className="text-brand-600 w-fit pl-2 pt-3"
              title={t('hotels.addCategory')}
              onClick={() => {
                append({
                  roomTypes: {
                    individual: false,
                    double: false,
                    triple: false,
                    quadruple: false,
                  },
                  images: [],
                  individualRoomOptions: [0],
                  doubleRoomOptions: [0],
                  tripleRoomOptions: [],
                  quadrupleRoomOptions: [],
                  categoryPensionPricings: [buildEmptyPensionCard()],
                  tarifs: [
                    {
                      periods: [{ applicationDays: WEEKDAYS }],
                      pricePartitions: {
                        individual: [],
                        double: [],
                        triple: [],
                        quadruple: [],
                      },
                    },
                  ],
                } as unknown as HotelFormData['hotelRoomCategories'][number]);
                appendTranslation(() => ({
                  name: undefined,
                  shortDescription: undefined,
                  longDescription: undefined,
                }));
                setUserSelectedIndex(fields.length);
              }}
              variant="link"
              icon={<PlusIcon className="size-5 fill-brand-600" />}
            />
          )}
        </div>
        {selectedCategoryIndex >= 0 && (
          <div
            className={clsx(
              'flex flex-col gap-4 w-full',
              isExpertMode ? EXPERT_FORM_COLUMN_WIDTH : FORM_COLUMN_WIDTH,
            )}
          >
            <HotelRoomCategoryForm
              control={control}
              index={selectedCategoryIndex}
              isViewMode={isViewMode}
              key={`room-category-${selectedCategoryIndex}`}
              language={language}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelRoomsCategoriesList;
